<script>
	const pubsub = require('events/pubsub');
	const _ = require('underscore');
	const Vue = require('vue');
	const Util = require('utilities');
	const model = require('model/model');
	const PromiseQueue = require('async/PromiseQueue');
	const assignmentService = require('services/assignmentService');
	const Q = require('q');
	const userContext = require('core/userContext');
	const articleContentMixin = require('mixins/article-content');
	const logger = require('utilities/logger');

	let _id = 0;

	module.exports = {
		mixins: [articleContentMixin],
		components: {
			highlighting: require('components/highlighting.vue').default
		},
		props: {
			content: {
				type: String,
				required: true,
				default: ''
			},
			'allow-edit': {
				type: Boolean,
				required: false,
				default: false
			},
			uuid: {
				type: String,
				required: true
			},
			'assignment-id': {
				type: String,
				required: true
			},
			'student-uid': {
				type: String,
				required: false
			},
			minimal: {
				default: false
			},
			persist: {
				default: true
			},
			paragraphsNumbered: {
				default: false
			},
			language: {
				type: String
			}
		},
		modelData: ['splitScreen'],
		data: function () {
			return {
				showingPrompt: null,
				// highlights -          this is an array of annotations, changes to this array mean
				//                       we need to push changes to the server. Highlights appear in
				//                       this array in the order the user created them.
				//
				highlights: [],
				sideNotes: [],
				noteTopByIdx: {},
				contentByIdx: {},
				// This is the note whose textarea should have focus right now
				noteBeingEdited: null,

				// this is the note that should show the delete prompt (even if its textarea isn't focused)
				showNoteDeleteFor: null,

				mobileModalId: 'modal-mobile-annotation-edit-' + _id++,
				mobileForm: {
					isNew: false,
					content: '',
					textNote: '',
					style: ''
				}
			};
		},
		created: function () {
			// As annotations are created in the highlighting component they're given IDs
			// of "newNN" where NN is a counter. After the API returns we set the ID to a
			// real value from the server. The trouble is, if an update is in the queue
			// for a new annotation then it doesn't have the real ID. This data structure
			// keeps track of what the "new" IDs became so that the updates in the queue
			// can look them up.
			//
			// To avoid having this structure grow forever, we clear it out when the queue
			// is empty, since we know then that it's definitely not needed
			this.newIdToRealId = {};

			this.apiCallQueue = new PromiseQueue();
		},
		mounted() {
			this.$watch('highlights', this.updateSideNotes.bind(this), {
				deep: true
			});

			this.$subscribe(
				'modal-closed',
				function (id) {
					if (id === this.mobileModalId) {
						var idx = this.noteBeingEdited;
						if (!this.highlights[idx].textNote) {
							Vue.set(
								this.highlights,
								idx,
								Object.assign({}, this.highlights[idx], {
									textNote: null
								})
							);
						}
						this.noteBeingEdited = null;
					}
				}.bind(this)
			);

			Vue.nextTick(() => {
				// hold off on setting up the Marklib stuff (renderAllHighlights) until DOM guaranteed

				this.own(
					pubsub.subscribe('annotation-position-' + this.$refs.content.id, this.positionChange.bind(this)),
					pubsub.subscribe('annotation-content-' + this.$refs.content.id, this.annotationContent.bind(this)),
					pubsub.subscribe('annotation-prompt-to-edit-' + this.$refs.content.id, this.editNote.bind(this)),
					pubsub.subscribe('annotation-update-' + this.$refs.content.id, _.partial(this.addParamsToAPIAndPushToQueue.bind(this), 'update')),
					pubsub.subscribe('annotation-remove-' + this.$refs.content.id, _.partial(this.addParamsToAPIAndPushToQueue.bind(this), 'remove')),
					pubsub.subscribe('annotation-create-' + this.$refs.content.id, _.partial(this.addParamsToAPIAndPushToQueue.bind(this), 'create')),
					pubsub.subscribe('annotation-create-' + this.$refs.content.id, this.sortSoAnnotationsNotCovered.bind(this)),

					pubsub.subscribe(
						'annotation-prompt-close-' + this.$refs.content.id,
						function () {
							this.showNoteDeleteFor = null;
						}.bind(this)
					),
					pubsub.subscribe(
						'annotation-prompt-open-' + this.$refs.content.id,
						function (idx) {
							this.showNoteDeleteFor = idx;
						}.bind(this)
					)
				);
			});
		},
		computed: {
			parsedContent() {
				return Util.cleanVocabLinks(this.content);
			},
			captionTTSEventData() {
				return {
					pid: this.uuid,
					aid: this.assignmentId
				};
			}
		},
		methods: {
			/**
			 * When we create a new annotation, make sure the sort order is such that annotations don't
			 * cover each other
			 */
			sortSoAnnotationsNotCovered: function () {
				var moved = false;
				var h2 = Util.cloneDeep(this.highlights);

				var recheck = true;
				var tries = 0;
				while (recheck && tries < 100) {
					recheck = false;
					tries++;

					for (var check = 0; check < h2.length; check++) {
						var covered = false;

						for (var later = check + 1; later < h2.length; later++) {
							if (h2[later].start <= h2[check].start && h2[later].end >= h2[check].end) {
								covered = true;
								recheck = true;
								break;
							}
						}

						if (covered) {
							covered = false;
							var tmp = h2[check];
							h2[check] = h2[check + 1];
							h2[check + 1] = tmp;
							moved = true;
						}
					}
				}

				if (moved) {
					this.highlights = h2;
				}
			},
			queueFailed: function (err) {
				err = err || {};

				if (err.status !== 401) {
					var exp = model.get('tokenExpiration');

					// log the moment the student logged in minus 12 hours or maybe just that
					// the tokenExpiration is undefined. If we know the exact moment of login
					// on this machine it could help determine if students are deleting annotations
					// on one device and then trying to update on another (trying to make sense
					// of logs that indicate this is a possibility)
					if (exp) {
						var signIn = new Date(parseInt(exp) - 60 * 60 * 12 * 1000);
						logger.info('queueFailed user sign in was at', signIn.toString());
					} else {
						logger.info('queueFailed, tokenExpiration is', exp);
					}

					// we'll also log highlights before and after we reloaded, which might help
					// determine which highlight was wrong
					logger.info(
						'About to try reloading all highlights current ids are: ',
						this.highlights
							.map(function (h) {
								return h.id;
							})
							.join(', ')
					);

					this.apiCallQueue.reset();
					this.loadHighlights().then(
						function (r) {
							logger.info(
								'Reloaded highlights current ids are: ',
								this.highlights
									.map(function (h) {
										return h.id;
									})
									.join(', ')
							);

							logger.error('queueFailed with code', err.code, 'status', err.status);
							return r;
						}.bind(this)
					);
				}
			},
			pushToQueue: function (which, params) {
				var entry = function () {
					logger.info('Performing', which, 'of note', params.id);
					if (which === 'update') {
						return this.updateAnnotation(params);
					} else if (which === 'remove') {
						return this.removeAnnotation(params);
					} else if (which === 'create') {
						return this.createAnnotation(params);
					} else {
						return Q();
					}
				}.bind(this);

				if (this.persist) {
					logger.info('Queueing', which, 'of note', params.id);
					this.apiCallQueue.push(entry, this.queueFailed.bind(this));
				}
			},
			addParamsToAPIAndPushToQueue: function (which, params) {
				if (!this.allowEdit) {
					return;
				}

				params = this.addParamsToAPI(params);
				this.pushToQueue(which, params);
			},

			/**
			 * While the queue is active the student may be navigating to different
			 * texts. This method adds the uuid and assignmentId to the params object
			 * so that navigation will not break any queue entries
			 *
			 * @param {[type]} which  [description]
			 * @param {[type]} params [description]
			 */
			addParamsToAPI: function (params) {
				params = Util.cloneDeep(params);

				// uuid or assignment ID might get changed by user navigation
				if (!params.productId) {
					params.productId = this.uuid;
				}

				if (!params.assignmentId) {
					params.assignmentId = this.assignmentId;
				}

				return params;
			},
			updateAnnotation: function (params) {
				if (!params.textNote) {
					delete params.textNote;
				}

				// we're not allowed to modify start and end
				delete params.start;
				delete params.end;

				// tell the API we deleted this
				if (params.noteJustRemovedNow) {
					params.textNote = '';
					delete params.noteJustRemovedNow;
				}

				if (params.id.substring(0, 3) === 'new') {
					// find what the new ID became
					params.id = this.newIdToRealId[params.id];
				}

				return assignmentService.updateAnnotation(params);
			},
			removeAnnotation: function (params) {
				var id = params.id;

				if (id.substring(0, 3) === 'new') {
					id = this.newIdToRealId[id];
				}

				return assignmentService.deleteAnnotation({
					assignmentId: params.assignmentId,
					productId: params.productId,
					id: id
				});
			},
			createAnnotation: function (params) {
				if (!params.textNote) {
					delete params.textNote;
				}

				var newId = params.id;
				delete params.id;

				return assignmentService.createAnnotation(params).then(
					function (result) {
						var id = Object.keys(result)[0];

						this.newIdToRealId[newId] = id;

						this.highlights.forEach(function (h) {
							if (h.id === newId) {
								Vue.set(h, 'id', id);
								Vue.set(h, 'created', result[id].created);
							}
						});

						return result;
					}.bind(this)
				);
			},
			loadHighlights: function () {
				var self = this;

				this.highlights = [];

				if (self.studentUid && self.assignmentId) {
					return assignmentService
						.teacherGetAnnotations({
							assignmentId: self.assignmentId,
							student: self.studentUid
						})
						.then(function (annotations) {
							var annotationsForProduct = annotations[self.uuid];

							var highlights = [];
							Object.keys(annotationsForProduct).forEach(function (id) {
								var h = annotationsForProduct[id];
								h.id = id;
								if (h.start && h.end) {
									highlights.push(h);
								}
							});

							self.highlights = highlights;

							self.sortSoAnnotationsNotCovered();
						});
				} else if (self.uuid && self.assignmentId && self.persist) {
					var uuidBeforeAPICall = self.uuid;
					return assignmentService
						.studentGetAnnotations({
							assignmentId: self.assignmentId,
							productId: self.uuid
						})
						.then(function (annotationsForProduct) {
							var highlights = [];

							// UUID changed while we were waiting, don't load these annotations.
							if (self.uuid !== uuidBeforeAPICall) {
								return annotationsForProduct;
							}

							Object.keys(annotationsForProduct).forEach(function (id) {
								var h = annotationsForProduct[id];
								h.id = id;
								if (h.start && h.end) {
									highlights.push(h);
								}
							});

							self.highlights = highlights;
							self.sortSoAnnotationsNotCovered();

							return null;
						});
				} else {
					return Q();
				}
			},
			annotationContent: function (params) {
				if (!params.content) {
					Vue.delete(this.contentByIdx, params.idx);
				} else {
					Vue.set(this.contentByIdx, params.idx, params.content);
				}
			},
			positionChange: function (position) {
				if (position.top === null) {
					Vue.delete(this.noteTopByIdx, position.idx);
				} else {
					Vue.set(this.noteTopByIdx, position.idx, position.top);
				}
			},
			editNoteMobileClick: function (idx) {
				this.editNote({
					idx: idx,
					isMobile: true
				});
			},
			editNote(params) {
				const { idx } = params;
				if (!this.highlights[idx].textNote) {
					Vue.set(this.highlights[idx], 'textNote', '');
				}

				let splitScreen = this.splitScreen;
				this.noteBeingEdited = idx;

				if (params.isMobile || splitScreen) {
					this.mobileForm.style = this.highlights[idx].style;
					this.mobileForm.textNote = this.highlights[idx].textNote || '';
					this.mobileForm.content = this.contentByIdx[idx];
					this.mobileForm.isNew = !this.highlights[idx].textNote;

					Util.openModal('#' + this.mobileModalId);
				}
			},
			saveMobileNote: function () {
				var idx = this.noteBeingEdited;
				const textNote = this.mobileForm.textNote || null;
				const updatedHighlight = Object.assign({}, this.highlights[idx], {
					textNote
				});
				Vue.set(this.highlights, idx, updatedHighlight);

				pubsub.publish('annotation-set-style-' + this.$refs.content.id, {
					idx: idx,
					style: this.mobileForm.style
				});

				Util.closeModals();
			},
			deleteMobileNote: function () {
				var idx = this.noteBeingEdited;
				Vue.set(
					this.highlights,
					idx,
					Object.assign({}, this.highlights[idx], {
						textNote: null
					})
				);

				Util.closeModals();
				pubsub.publish(
					'annotation-update-' + this.$refs.content.id,
					_.extend(
						{
							noteJustRemovedNow: true
						},
						this.highlights[idx]
					)
				);
			},
			deleteNote: function (idx) {
				Vue.set(
					this.highlights,
					idx,
					Object.assign({}, this.highlights[idx], {
						textNote: null
					})
				);
				this.noteBeingEdited = null;
				this.showNoteDeleteFor = null;
				pubsub.publish(
					'annotation-update-' + this.$refs.content.id,
					_.extend(
						{
							noteJustRemovedNow: true
						},
						this.highlights[idx]
					)
				);
			},
			updateSideNotes() {
				const newSideNotes = this.highlights.reduce((acc, h, idx) => {
					if (typeof h.textNote === 'string') {
						let textNote = h.textNote;

						if (idx === this.noteBeingEdited && !textNote) {
							// prevent note reset from async update on creation
							const snBeingEdited = this.sideNotes.find(sn => sn.idx === idx);
							if (snBeingEdited && typeof snBeingEdited.textNote === 'string') {
								textNote = snBeingEdited.textNote;
							}
						}

						acc.push({
							start: h.start,
							idx,
							textNote
						});
					}

					return acc;
				}, []);

				this.sideNotes = Util.sortObjects(newSideNotes, 'start');
			},
			tabCycle: function (idx, event) {
				var next = this.sideNotes[(idx + 1) % this.sideNotes.length].idx;
				if (event) {
					event.preventDefault();
				}

				this.editNote({
					idx: next,
					isMobile: false
				});
			},
			blurUpdateNote: function (sideNote) {
				var h = this.highlights[sideNote.idx];

				// user deleted note while textarea was focused
				if (!h) {
					return;
				}

				// only send an update if the note changed
				if (h.textNote !== sideNote.textNote) {
					if (!sideNote.textNote) {
						sideNote.textNote = null;
					}

					// The blur may have happened because the user clicked "delete"
					// so let's wait 0.1s and make sure h.textNote doesn't change, if it
					// does then that means it was probably deleted and we shouldn't send
					// a (redundant) update from here
					var noteAtBlur = h.textNote;
					var params = this.addParamsToAPI(
						_.extend({}, h, {
							textNote: sideNote.textNote
						})
					);

					setTimeout(
						function () {
							// make sure the note wasn't just deleted
							if (noteAtBlur !== h.textNote) {
								return;
							}

							this.showNoteDeleteFor = null;
							h.textNote = sideNote.textNote;
							var justRemoved = !!(h.textNote && !sideNote.textNote);
							params = _.extend(
								{
									noteJustRemovedNow: justRemoved
								},
								params
							);
							this.pushToQueue('update', params);
						}.bind(this),
						100
					);
				} else if (!h.textNote) {
					// we just blurred when the note is blank, so get rid of it
					Vue.set(h, 'textNote', null);
					Vue.set(sideNote, 'textNote', null);
				}

				// we need noteBeingEdited to change to focus the textareas
				// so on blur we have to set it to -1. Otherwise repeated clicks
				// to edit the same note won't work
				this.noteBeingEdited = -1;
			},
			openAnnotationPrompt: function (idx) {
				pubsub.publish('annotation-prompt-' + this.$refs.content.id, idx);
			},
			focusedNote: function (idx) {
				this.noteBeingEdited = idx;
				pubsub.publish('annotation-prompt-' + this.$refs.content.id, idx);
			},
			loadWatcher() {
				const isStudentLogin = !!userContext.isStudent();
				let ready = false;

				this.highlights = [];

				if (!this.persist) {
					ready = true;
				} else if (isStudentLogin) {
					ready = !!(this.uuid && this.assignmentId);
				} else {
					// teacher side
					ready = !!(this.uuid && this.studentUid);
				}

				if (ready) {
					this.loadHighlights();
				}
			}
		},
		watch: {
			uuid: 'loadWatcher',
			studentUid: 'loadWatcher',
			assignmentId: {
				handler: 'loadWatcher',
				immediate: true
			}
		}
	};
</script>
<template>
	<div
		class="annotated-text"
		:class="{
			'side-notes': sideNotes.length,
			'force-mobile': !persist
		}">
		<article
			:class="{
				'numbered-paragraphs': paragraphsNumbered
			}">
			<highlighting
				ref="content"
				:highlights="highlights"
				:allow-edit="allowEdit"
				:minimal="minimal"
				:content="parsedContent"
				:split-screen="splitScreen"
				:force-mobile="!persist">
			</highlighting>
		</article>
		<div
			class="note-container"
			ref="noteContainer">
			<div
				v-for="(sn, snIndex) in sideNotes"
				:key="sn.idx"
				v-if="highlights[sn.idx]"
				v-on:click.stop="openAnnotationPrompt(sn.idx)"
				v-margin-down-to="noteTopByIdx[sn.idx]"
				:class="{
					note: true,
					'note-o': highlights[sn.idx].style === 'o',
					'note-g': highlights[sn.idx].style === 'g',
					'note-p': highlights[sn.idx].style === 'p',
					'note-b': highlights[sn.idx].style === 'b'
				}">
				<textarea
					v-if="allowEdit"
					v-focus-if="noteBeingEdited === sn.idx"
					style="width: 200px"
					maxlength="600"
					v-model="sn.textNote"
					v-autosize-textarea="sn.textNote"
					v-on:blur="blurUpdateNote(sn)"
					v-on:keydown.tab="tabCycle(snIndex, $event)"
					v-on:focus="focusedNote(sn.idx)"
					placeholder="Write something..."></textarea>
				<div
					v-else
					style="width: 200px">
					{{ highlights[sn.idx].textNote }}
				</div>
				<a
					v-if="allowEdit && highlights[sn.idx].textNote && (showNoteDeleteFor === sn.idx || noteBeingEdited === sn.idx)"
					v-on:click.prevent="deleteNote(sn.idx)">
					<i class="fa-solid fa-trash-can"></i> Delete
				</a>
				<div
					v-if="allowEdit && sn.textNote && sn.textNote.length === 600"
					class="error-text max-length">
					* Maximum 600 characters
				</div>
			</div>
			<div
				v-for="sn in sideNotes"
				:key="sn.idx"
				v-if="highlights[sn.idx]"
				v-on:click.stop="editNoteMobileClick(sn.idx)"
				v-margin-down-to="noteTopByIdx[sn.idx]"
				:class="{
					'mobile-note': true,
					'note-o': highlights[sn.idx].style === 'o',
					'note-g': highlights[sn.idx].style === 'g',
					'note-p': highlights[sn.idx].style === 'p',
					'note-b': highlights[sn.idx].style === 'b'
				}">
				<note-icon classes="note-edit-icon"></note-icon>
			</div>
		</div>
		<div
			:id="mobileModalId"
			class="modal modal-mobile-annotation-edit"
			data-modal-window>
			<div class="modal-container">
				<div class="modal-window">
					<div class="modal-window-wrap">
						<section class="modal-content">
							<header class="modal-content-header">
								<a
									:class="{ highlight: true, orange: true, selected: mobileForm.style === 'o' }"
									v-on:click="mobileForm.style = 'o'">
								</a>
								<a
									:class="{ highlight: true, green: true, selected: mobileForm.style === 'g' }"
									v-on:click="mobileForm.style = 'g'">
								</a>
								<a
									:class="{ highlight: true, purple: true, selected: mobileForm.style === 'p' }"
									v-on:click="mobileForm.style = 'p'">
								</a>
								<a
									:class="{ highlight: true, blue: true, selected: mobileForm.style === 'b' }"
									v-on:click="mobileForm.style = 'b'">
								</a>
							</header>
							<div class="modal-content-main">
								<div class="annotation-container">
									<div
										class="content-excerpt"
										:class="{
											'style-o': mobileForm.style === 'o',
											'style-g': mobileForm.style === 'g',
											'style-b': mobileForm.style === 'b',
											'style-p': mobileForm.style === 'p'
										}"
										v-html="mobileForm.content"></div>
									<div
										class="textarea-wrapper"
										:class="{ 'max-length': mobileForm.textNote.length === 600 }">
										<textarea
											v-model="mobileForm.textNote"
											maxlength="600"
											placeholder="Write something...">
										</textarea>
									</div>
									<div
										v-if="mobileForm.textNote.length === 600"
										class="error-text max-length">
										* Maximum 600 characters
									</div>
									<div class="form-row">
										<div class="form-row-input">
											<template v-if="mobileForm.isNew">
												<button
													type="submit"
													class="button -large -full-width"
													v-on:click.prevent="saveMobileNote">
													Add Note
												</button>
											</template>
											<template v-else>
												<button
													type="submit"
													class="button -large -right -outline"
													v-on:click.prevent="deleteMobileNote">
													Delete Note
												</button>
												<button
													type="submit"
													class="button -large"
													v-on:click.prevent="saveMobileNote">
													Save
												</button>
											</template>
										</div>
									</div>
								</div>
							</div>
						</section>
					</div>
				</div>
			</div>
		</div>
		<!-- end #modal-mobile-annotation-edit -->
	</div>
</template>
